import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FiArrowLeft, FiUpload, FiDownload, FiInfo } from 'react-icons/fi';
import { getAllPartners, importReferencesSubcoverages } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { buildPartnerSelectOptions } from '../../utils/partnerId';
import {
    extractReferencesSubcovImportError,
    parseReferencesSubcovImportResponse,
    TRIP_REF_SUBCOVERAGES_IMPORT_TEMPLATE_URL,
} from '../../utils/referencesSubcovImport';
import ReferencesSubcovImportResultAlert from '../../components/Admin/ReferencesSubcovImportResultAlert';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminReference.css';

const ENDPOINT_LABEL = 'POST /api/trips/import-references-subcov/partners/{partner_id}';

const AdminReferenceSubcoveragesImport = () => {
    const navigate = useNavigate();
    const [partners, setPartners] = useState([]);
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [partnersError, setPartnersError] = useState(null);
    const [importFile, setImportFile] = useState(null);
    const [partnerId, setPartnerId] = useState('');
    const [importLoading, setImportLoading] = useState(false);
    const [importError, setImportError] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const resultRef = useRef(null);

    const partnerSelectOptions = useMemo(
        () => buildPartnerSelectOptions(partners),
        [partners]
    );

    const selectedPartnerLabel = useMemo(() => {
        if (!partnerId) return null;
        return partnerSelectOptions.find((option) => option.id === partnerId)?.label ?? null;
    }, [partnerId, partnerSelectOptions]);

    useEffect(() => {
        const loadPartners = async () => {
            try {
                setPartnersLoading(true);
                setPartnersError(null);
                const response = await getAllPartners();
                const partnersArray = response?.users && Array.isArray(response.users)
                    ? response.users
                    : (Array.isArray(response) ? response : []);
                setPartners(partnersArray);
            } catch (error) {
                setPartners([]);
                setPartnersError(handleApiError(error, 'Erreur lors du chargement des partenaires'));
            } finally {
                setPartnersLoading(false);
            }
        };
        loadPartners();
    }, []);

    useEffect(() => {
        if (importResult && resultRef.current) {
            resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [importResult]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!importFile || !partnerId) {
            setImportError('Veuillez sélectionner un fichier et un partenaire');
            return;
        }

        setImportLoading(true);
        setImportError(null);
        setImportResult(null);

        try {
            const result = await importReferencesSubcoverages(partnerId, importFile);
            const parsed = parseReferencesSubcovImportResponse(result);
            if (parsed) {
                setImportResult(parsed);
            } else {
                setImportResult({
                    message: typeof result === 'string'
                        ? result
                        : 'Import réussi ! Les références et sous-couvertures ont été importées.',
                });
            }
        } catch (error) {
            console.error('Erreur lors de l\'import:', error);
            const parsed = parseReferencesSubcovImportResponse(error.response?.data);
            if (parsed) {
                setImportResult(parsed);
            } else {
                setImportError(extractReferencesSubcovImportError(error));
            }
        } finally {
            setImportLoading(false);
        }
    };

    const handleReset = () => {
        setImportFile(null);
        setPartnerId('');
        setImportError(null);
        setImportResult(null);
    };

    return (
        <div className="dashboard-page admin-reference-page">
            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                        <div>
                            <h2 className="page-title">
                                <FiUpload className="me-2" />
                                Import ref et subcoverage
                            </h2>
                            <p className="text-muted mb-0">
                                Importez un fichier Excel de références et sous-couvertures trip pour un partenaire.
                            </p>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button
                                variant="outline-primary"
                                href={TRIP_REF_SUBCOVERAGES_IMPORT_TEMPLATE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                as="a"
                                className="d-inline-flex align-items-center"
                            >
                                <FiDownload className="me-2" aria-hidden />
                                Télécharger le template
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={() => navigate('/admin/reference')}
                                className="d-inline-flex align-items-center"
                            >
                                <FiArrowLeft className="me-2" aria-hidden />
                                Retour
                            </Button>
                        </div>
                    </div>

                    {importLoading && (
                        <Alert variant="info" className="mb-4 d-flex align-items-center gap-2">
                            <Spinner size="sm" animation="border" aria-hidden />
                            <span>
                                <strong>Import en cours…</strong> Cela peut prendre plusieurs minutes selon la taille du fichier.
                            </span>
                        </Alert>
                    )}

                    <Card className="mb-4" style={{ border: '1px solid #e9ecef', borderRadius: '8px' }}>
                        <Card.Body>
                            <Alert variant="light" className="border mb-4">
                                <div className="d-flex align-items-start gap-2">
                                    <FiInfo className="mt-1 flex-shrink-0" aria-hidden />
                                    <div className="flex-grow-1">
                                        <p className="text-muted small mb-2">
                                            <strong>Endpoint :</strong>{' '}
                                            <code className="text-break">{ENDPOINT_LABEL}</code>
                                        </p>
                                        <p className="small mb-0">
                                            Sélectionnez le partenaire cible puis chargez le fichier Excel au format template import.
                                        </p>
                                    </div>
                                </div>
                            </Alert>

                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        Partenaire <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        value={partnerId}
                                        onChange={(e) => {
                                            setPartnerId(e.target.value);
                                            setImportError(null);
                                        }}
                                        disabled={importLoading || partnersLoading}
                                        required
                                    >
                                        <option value="">Sélectionner un partenaire…</option>
                                        {partnerSelectOptions.map(({ id, label }) => (
                                            <option key={id} value={id}>
                                                {label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    {partnerId && (
                                        <Form.Text className="text-muted d-block mt-1">
                                            Partenaire sélectionné : <strong>{selectedPartnerLabel || '—'}</strong>
                                            {' · '}
                                            ID API : <code>{partnerId}</code>
                                        </Form.Text>
                                    )}
                                    {partnersError && (
                                        <div className="text-danger small mt-1">{partnersError}</div>
                                    )}
                                </Form.Group>

                                <Form.Group className="mb-4">
                                    <Form.Label className="fw-bold">
                                        Fichier Excel <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="file"
                                        accept=".xlsx,.xls"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0] || null;
                                            setImportFile(file);
                                            setImportError(null);
                                            setImportResult(null);
                                        }}
                                        disabled={importLoading}
                                        required
                                    />
                                    <Form.Text className="text-muted">
                                        Fichier Excel (.xlsx ou .xls) contenant les références et sous-couvertures à importer.
                                    </Form.Text>
                                    {importFile && (
                                        <div className="mt-2 small text-success">
                                            Fichier sélectionné : {importFile.name}
                                        </div>
                                    )}
                                </Form.Group>

                                <div className="d-flex justify-content-end gap-2">
                                    <Button
                                        type="button"
                                        variant="outline-secondary"
                                        onClick={handleReset}
                                        disabled={importLoading}
                                    >
                                        Réinitialiser
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={importLoading || !importFile || !partnerId}
                                    >
                                        {importLoading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" animation="border" aria-hidden />
                                                Import en cours…
                                            </>
                                        ) : (
                                            <>
                                                <FiUpload className="me-2" aria-hidden />
                                                Importer
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>

                    {importError && (
                        <Alert variant="danger" className="mb-4" dismissible onClose={() => setImportError(null)}>
                            <strong>Erreur :</strong> {importError}
                        </Alert>
                    )}

                    {importResult && (
                        <div ref={resultRef}>
                            <ReferencesSubcovImportResultAlert
                                result={importResult}
                                onDismiss={() => setImportResult(null)}
                            />
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminReferenceSubcoveragesImport;
